/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import Svg from "@natera/material/lib/svg";
import { Document, Page } from "react-pdf";
import type { File } from "react-pdf/dist/cjs/shared/types";
import { Button } from "@natera/form";
import { defineMessages, useIntl } from "react-intl";

import { IntlContext } from "@app/provider";

import CloudError from "@assets/svg/icons/cloud-error.svg";
import { ScrollContext } from "@natera/platform/lib/components/scrollbar";

import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import "./pdf.scss";
import { usePdfContainerWidth } from "./usePdfContainerWidth";

const messages = defineMessages({
  pdfResultsDocument: {
    id: "pdfResultsDocument",
    defaultMessage: "Result Details",
  },
  pdfYourResults: {
    id: "pdfyourResults",
    defaultMessage: "Your Results",
  },
  pdfDownload: {
    id: "pdfDownload",
    defaultMessage: "Download",
  },
  pdfLoadError: {
    id: "pdfLoadError",
    defaultMessage: "Sorry, we cannot retrieve results right now.",
  },
  pdfSubLoadError: {
    id: "pdfSubLoadError",
    defaultMessage: "Please try again later.",
  },
  pdfCloseResults: {
    id: "pdfCloseResults",
    defaultMessage: "Close results",
  },
  pdfDefaultAriaLabel: {
    id: "pdfDefaultAriaLabel",
    defaultMessage: "Document",
  },
  pdfPage: {
    id: "pdfPage",
    defaultMessage: "Page",
  },
});

type onDocumentLoadSuccess = (pdf: any) => Promise<void>;
type onPageLoadSuccess = (pdf: any) => Promise<void>;
type addAttributesToCanvas = (pageNumber: number) => void;

type Props = {
  file: File | null;
  onDownload?: () => Promise<void>;
  onPdfViewError?: () => void;
  onPdfViewSuccess?: () => void;
  pageAriaLabel?: string;
  hasError?: boolean;
};

const PdfView: React.FC<Props> = ({
  file,
  onDownload,
  onPdfViewError,
  onPdfViewSuccess,
  pageAriaLabel,
  hasError = false,
}) => {
  const intl = useIntl();
  const { currentLanguage } = React.useContext(IntlContext);
  const [isError, setError] = React.useState<boolean>(hasError);
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [pdfWidth, setPdfWidth] = React.useState<number>();

  const refPdfContainer = React.useRef<HTMLDivElement>(null);

  usePdfContainerWidth(refPdfContainer, setPdfWidth);

  React.useEffect(() => {
    if (isError && onPdfViewError) {
      onPdfViewError();
    }
  }, [isError, onPdfViewError]);

  const onDocumentLoadSuccess: onDocumentLoadSuccess = async ({
    numPages: nextNumPages,
  }) => {
    setError(false);
    setNumPages(nextNumPages);
    onPdfViewSuccess && onPdfViewSuccess();
  };

  const onPageLoadSuccess: onPageLoadSuccess = async (page) => {
    const containerWidth = refPdfContainer.current?.clientWidth;

    if (!containerWidth) {
      return;
    }

    setPdfWidth(containerWidth);
  };

  const addAttributesToCanvas: addAttributesToCanvas = (pageNumber) => {
    const canvasElement = document.querySelector<HTMLCanvasElement>(
      `.react-pdf__Page[data-page-number="${pageNumber}"] canvas`
    );
    if (canvasElement) {
      canvasElement.setAttribute("role", "img");
      canvasElement.setAttribute(
        "aria-label",
        `${
          pageAriaLabel || intl.formatMessage(messages.pdfDefaultAriaLabel)
        }. ${intl.formatMessage(messages.pdfPage)} ${pageNumber}`
      );
      canvasElement.setAttribute("tabindex", "0");
    }
  };

  React.useEffect(() => {
    // We could use onRenderSuccess instead, but for some reason it makes changing of language to slow.
    const interval = setInterval(() => {
      const renderedCanvas = document.querySelectorAll(
        ".react-pdf__Page canvas"
      ).length;

      if (renderedCanvas === numPages && numPages > 0) {
        for (let page = 1; page <= numPages; page++) {
          addAttributesToCanvas(page);
        }
        clearInterval(interval);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [numPages, currentLanguage]);

  const onError = () => {
    setError(true);
  };

  return (
    <section className="pdf__container" ref={refPdfContainer}>
      <div className="pdf__container__header">
        {!isError && onDownload && (
          <div className="pdf__container__header__title">
            <Button
              className="pdf__container__header__download"
              unelevated
              onClick={onDownload}
            >
              {intl.formatMessage(messages.pdfDownload)}
            </Button>
          </div>
        )}
      </div>
      <ScrollContext
        component="div"
        isStatic={false}
        settings={{ suppressScrollX: true }}
      >
        {!isError && (
          <div>
            <Document
              className="pdf__container__header__result-pdf"
              file={file}
              onLoadError={onError}
              onLoadSuccess={onDocumentLoadSuccess}
              onSourceError={onError}
            >
              {Array.from(new Array(numPages), (el, index) => (
                <Page
                  key={`page_${index + 1}`}
                  className="pdf__container__header__result-pdf-page"
                  pageNumber={index + 1}
                  renderTextLayer
                  renderAnnotationLayer
                  width={pdfWidth}
                  onLoadSuccess={onPageLoadSuccess}
                />
              ))}
            </Document>
          </div>
        )}
        {isError && (
          <div className="pdf__container__error">
            <div className="image">
              <Svg content={CloudError} />
            </div>
            <div className="main-text">
              <span>{intl.formatMessage(messages.pdfLoadError)}</span>
            </div>
            <div className="sub-text">
              <span>{intl.formatMessage(messages.pdfSubLoadError)}</span>
            </div>
          </div>
        )}
      </ScrollContext>
      <div className="pdf__container__footer">
        {!isError && (
          <Button
            className="pdf__container__footer__download"
            unelevated
            onClick={onDownload}
          >
            {intl.formatMessage(messages.pdfDownload)}
          </Button>
        )}
      </div>
    </section>
  );
};

export default PdfView;
